import {createSandboxedIframe, sanitizeEmailHTML} from "../emailUtils"
import {EmailMessage} from "@/utils/graphQL/queries/threads/shared"

import {Checkbox, Tooltip} from "@heroui/react"
import {useEffect, useState} from "react"
import {twMerge} from "tailwind-merge"

interface MessageTableItemViewProps {
  sender: string[]
  receiver: string[]
  message: string
  subject: string
  raw_content: string
  sentAt: string
  cc: string[]
  bcc: string[]
  idx: number
}

const MessageTableItemView = ({
  sender,
  receiver,
  message,
  subject,
  raw_content,
  sentAt,
  cc,
  bcc,
  idx,
}: MessageTableItemViewProps) => {
  const [contentView, setContentView] = useState<"text" | "html">("text")
  const [sanitizedContent, setSanitizedContent] = useState<string>("")
  const [messageContent, setMessageContent] = useState<string>(message)
  const [iframeSrc, setIframeSrc] = useState<string | null>(null)
  const [hasBothContentTypes, setHasBothContentTypes] = useState<boolean>(false)

  // Format date for easier reading
  const formattedDate = new Date(sentAt).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })

  useEffect(() => {
    const {sanitizedContent, prunedContent, iframeSrc} =
      sanitizeEmailHTML(raw_content)
    setMessageContent(message)
    if (sanitizedContent) {
      setSanitizedContent(sanitizedContent)
    } else {
      setSanitizedContent(message)
    }

    if (iframeSrc) {
      setIframeSrc(iframeSrc)
    }

    const hasTextContent = message && message.trim() !== ""
    const hasHtmlContent = sanitizedContent && sanitizedContent.trim() !== ""

    // Determine if we have both content types available
    setHasBothContentTypes(hasTextContent && hasHtmlContent)

    // If we only have HTML content and no text, automatically show HTML
    if (!hasTextContent && hasHtmlContent) {
      setContentView("html")
    }
  }, [message, raw_content])

  return (
    <div className="mb-4 flex flex-col overflow-hidden rounded border border-gray-200 bg-white transition-all duration-200 hover:translate-y-[-1px] hover:shadow-md">
      {/* Message Header */}
      <div className="border-b border-gray-200 bg-gray-50 p-3">
        <div className="flex justify-between">
          <div className="flex flex-col">
            <div className="mb-1 flex items-center gap-2">
              <span className="text-sm font-medium">{sender[0]}</span>
              <span className="text-xs text-gray-500">
                to {receiver[0]}
                {receiver.length > 1 ? ` +${receiver.length - 1}` : ""}
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span>{formattedDate}</span>
            </div>
          </div>

          {hasBothContentTypes && (
            <Tooltip
              content="Toggle between text and HTML view"
              placement="bottom-end"
            >
              <Checkbox
                size="sm"
                classNames={{
                  wrapper: "me-1",
                  label: "font-mono text-xs",
                }}
                isSelected={contentView === "html"}
                onValueChange={() => {
                  setContentView(contentView === "html" ? "text" : "html")
                }}
              >
                HTML
              </Checkbox>
            </Tooltip>
          )}
        </div>

        {/* Subject line - show for all messages */}
        <p className="mt-2 text-sm font-medium">{subject}</p>
      </div>

      {/* Message Content */}
      <div className="p-4">
        {contentView === "text" ? (
          <>
            {iframeSrc ? (
              /* Secure iframe rendering */
              <div className="min-h-[150px]">
                {createSandboxedIframe(iframeSrc)}
              </div>
            ) : (
              /* Text content view */
              <div className="whitespace-pre-line font-mono text-xs leading-relaxed text-gray-800">
                {messageContent || "Loading..."}
              </div>
            )}
          </>
        ) : (
          /* HTML content view */
          <div
            className="max-h-[400px] overflow-auto rounded bg-white p-2 font-mono text-xs"
            dangerouslySetInnerHTML={{
              __html: sanitizedContent,
            }}
          />
        )}
      </div>

      {/* Optional metadata section - collapsed by default */}
      {(cc.length > 0 || bcc.length > 0) && (
        <details className="border-t border-gray-200 bg-gray-50 px-4 py-2">
          <summary className="cursor-pointer text-xs text-gray-500">
            Show details
          </summary>
          <div className="mt-2 text-xs text-gray-600">
            <p className="mb-1">
              <span className="font-medium">From:</span> {sender.join(", ")}
            </p>
            <p className="mb-1">
              <span className="font-medium">To:</span> {receiver.join(", ")}
            </p>
            {cc.length > 0 && (
              <p className="mb-1">
                <span className="font-medium">CC:</span> {cc.join(", ")}
              </p>
            )}
            {bcc.length > 0 && (
              <p className="mb-1">
                <span className="font-medium">BCC:</span> {bcc.join(", ")}
              </p>
            )}
          </div>
        </details>
      )}
    </div>
  )
}

export default MessageTableItemView
